import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { statsWindow } from '@/shared/lib'

import type { LeadSlice } from '../model/slice'
import type { LeadFilters } from '../model/types'

import { fetchLead, fetchLeads } from './leads-api'
import { leadKeys } from './query-keys'

/**
 * Rows per page. The backend clamps anything above 200, so all three land as
 * asked; the chosen one lives in the route's search params, not here.
 */
export const LEADS_PAGE_SIZES = [25, 50, 100]

export const leadsPageQuery = (
  params: LeadFilters & { campaignId: string; page: number; pageSize: number },
) =>
  queryOptions({
    queryKey: leadKeys.list(params),
    queryFn: ({ signal }) =>
      fetchLeads(
        {
          ...params,
          limit: params.pageSize,
          offset: params.page * params.pageSize,
        },
        signal,
      ),
    // Paging feels instant and the table keeps its height between pages — and
    // typing in the search box does not blank it out on every keystroke.
    placeholderData: keepPreviousData,
  })

/**
 * The one place a leads query is built, and the reason it exists is the bug it
 * prevents.
 *
 * A route `loader` and the table it loads for have to produce the *same* key,
 * or the preload is fetched and discarded and the table asks again — a failure
 * with no error and no warning, visible only as the page changing size a frame
 * after it arrives. Three call sites were assembling that key by hand from a
 * slice, a time window and a page; they agreed by inspection until one of them
 * stopped agreeing.
 *
 * `statsWindow` rounds its upper bound up to the hour, which is what lets a
 * loader and a render milliseconds apart land on the same window. `campaignId`
 * is part of the key, so two campaigns' logs cannot collide.
 */
export const leadsQueryFor = (
  slice: LeadSlice,
  scope: { campaignId: string; page: number; pageSize: number },
) => {
  const { period, ...filters } = slice
  const window = statsWindow(period)

  return leadsPageQuery({
    ...filters,
    from: window.from,
    to: window.to,
    campaignId: scope.campaignId,
    page: scope.page,
    pageSize: scope.pageSize,
  })
}

export const leadQuery = (id: string) =>
  queryOptions({
    queryKey: leadKeys.detail(id),
    queryFn: ({ signal }) => fetchLead(id, signal),
  })
